import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./PrivateProfile.css";
import { fetchProfileById, updateProfile } from "../lib/profileApi";
import gilbraterImage from "../gilbrater.jpg";

function PrivateProfile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [description, setDescription] = useState("");
  const [editableImages, setEditableImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const syncProfileState = (result) => {
    setProfile(result);
    setDescription(result.description || "");
    setEditableImages(
      result.images && result.images.length > 0
        ? result.images
        : result.image
          ? [result.image]
          : [],
    );
  };

  useEffect(() => {
    const profileId = localStorage.getItem("authenticatedProfileId");

    if (!profileId) {
      navigate("/login");
      return;
    }

    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const result = await fetchProfileById(profileId);
        syncProfileState(result);
      } catch (error) {
        setStatusMessage(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [navigate]);

  const handleImageSelection = async (event) => {
    const files = Array.from(event.target.files || []);
    const encoded = await Promise.all(
      files.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () =>
              resolve(typeof reader.result === "string" ? reader.result : "");
            reader.readAsDataURL(file);
          })
      )
    );

    setNewImages(encoded.filter(Boolean));
  };

  const handleDeleteImage = (imageToDelete) => {
    setEditableImages((current) => current.filter((image) => image !== imageToDelete));
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!profile) {
      return;
    }

    try {
      setIsSaving(true);
      setStatusMessage("");
      const images = [...editableImages, ...newImages].filter(Boolean);
      const updated = await updateProfile(profile.id, {
        description,
        images,
      });
      const refreshed = await fetchProfileById(profile.id);
      syncProfileState(refreshed || updated);
      setNewImages([]);
      setStatusMessage("Private profile updated.");
    } catch (error) {
      setStatusMessage(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="private-profile-page">
        <div className="private-profile-shell">
          <h1>Loading profile...</h1>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="private-profile-page">
        <div className="private-profile-shell">
          <h1>Private profile not available</h1>
          <p>{statusMessage || "Please log in first."}</p>
          <Link className="private-profile-link" to="/login">
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  const gallery = editableImages.length > 0
    ? editableImages
    : [profile.image || gilbraterImage];

  return (
    <div className="private-profile-page">
      <div className="private-profile-shell">
        <div className="private-profile-main">
          <img
            className="private-profile-hero-image"
            src={gallery[0] || gilbraterImage}
            alt={profile.name}
          />

          <div className="private-profile-content">
            <p className="private-profile-kicker">Private Profile</p>
            <h1>{profile.name}</h1>
            <p className="private-profile-meta">Age: {profile.ageRange || "Not set"}</p>
            <p className="private-profile-description">{profile.description || "No description yet."}</p>
          </div>
        </div>

        <div className="private-profile-gallery">
          {gallery.map((image, index) => (
            <div key={`${profile.id}-${index}`} className="private-profile-gallery-card">
              <img
                className="private-profile-gallery-image"
                src={image || gilbraterImage}
                alt={`${profile.name} ${index + 1}`}
              />
              <button
                type="button"
                className="private-profile-delete-image"
                onClick={() => handleDeleteImage(image)}
                disabled={editableImages.length <= 1}
              >
                Delete image
              </button>
            </div>
          ))}
        </div>

        <form className="private-profile-editor" onSubmit={handleSave}>
          <label className="private-profile-label" htmlFor="description">
            Edit description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows="6"
          ></textarea>

          <label className="private-profile-label" htmlFor="images">
            Add more images
          </label>
          <input
            id="images"
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageSelection}
          />

          {statusMessage ? <p className="private-profile-status">{statusMessage}</p> : null}

          <div className="private-profile-actions">
            <button type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save changes"}
            </button>
            <Link className="private-profile-link" to="/">
              Back to home
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PrivateProfile;
